/**
 * Automations as a top-level page (`/:orgId/automations`).
 *
 * Automations used to live only as a settings section. They are not a setting
 * — they are something you build and then watch run — so they get their own
 * nav entry and their own route. Validation rules deliberately stay in
 * settings: those really are configuration.
 *
 * Renders through `CRMObjectListPage`, the same list every other CRM entity
 * uses, so search, filtering, saved views and the standard header come for
 * free rather than being rebuilt here and drifting.
 *
 * TWO WAYS TO CREATE, deliberately named apart:
 *   - "Add automation" (primary) — describe what you want; Ahoy builds it.
 *   - "Built-in automations" — a panel of ready-made ones we wrote and
 *     maintain. These are the ONLY way to get lead rotation, CRM ingestion or
 *     date reminders; the assistant cannot produce them.
 */

import { Dataflow03 } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import type { HeaderAction } from '@/lib/base/page-layout/page-layout';
import { BuiltInAutomationLibrary } from '@/lib/crm/automations/BuiltInAutomationLibrary';
import { buildCreateInBuilderNavState } from '@/lib/crm/components/badges/buildBuilderModeNavState';
import { buildAutomationListConfig, CRMObjectListPage } from '@/lib/crm/list';
import { logger } from '@/lib/logger';
import { useViewHealthMarker } from '@/lib/view-health';

import { useTitle } from '../../hooks/useTitle';

const CREATE_AUTOMATION_PROMPT = 'I want to build an automation';

export default function AutomationsPage() {
  useTitle('Automations');
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const { data: featureFlags } = useFeatureFlags(currentOrg?.id);
  // Internal automations are managed centrally and are not user-toggleable,
  // so they stay filtered out unless a staff member opts in.
  const showInternal = useFeatureFlag('internal_automations');
  const isBuilderModeEnabled = useFeatureFlag('builder_mode');
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  useViewHealthMarker('automations-list-ready', {
    ready: Boolean(currentOrg) && isBuilderModeEnabled,
  });

  const config = useMemo(
    () => buildAutomationListConfig({ showInternal }),
    [showInternal]
  );

  const activeFlags = useMemo(() => featureFlags ?? [], [featureFlags]);

  // An automation is described, not filled in — so the create action opens
  // the builder rather than a property form. This page redirects to the
  // settings automations section when `builder_mode` is off, so anyone who
  // reaches past this point has the flag and the builder is available to
  // them.
  const handleCreate = useCallback(() => {
    if (!currentOrg) {
      return;
    }
    const { to, state } = buildCreateInBuilderNavState(
      currentOrg.id,
      'ahoy_event_automation',
      CREATE_AUTOMATION_PROMPT
    );
    navigate(to, { state });
  }, [currentOrg, navigate]);

  const openLibrary = useCallback(() => setIsLibraryOpen(true), []);

  const extraHeaderActions: HeaderAction[] = useMemo(
    () => [
      {
        id: 'built-in-automations',
        label: 'Built-in automations',
        icon: Dataflow03,
        onClick: openLibrary,
      },
    ],
    [openLibrary]
  );

  if (currentOrg && !isBuilderModeEnabled) {
    logger.debug('Redirecting to settings automations (builder_mode off)', {
      orgId: currentOrg.id,
    });
    return <Navigate to={`/${currentOrg.id}/settings/automations`} replace />;
  }

  return (
    <>
      {/*
        `onCreateClick` rather than `onCreateObject`: the latter opens the
        default property form, which is the wrong shape for a record you
        describe rather than fill in. Both render the Add button.
      */}
      <CRMObjectListPage
        config={config}
        onCreateClick={handleCreate}
        extraHeaderActions={extraHeaderActions}
      />
      <BuiltInAutomationLibrary
        isOpen={isLibraryOpen}
        onOpenChange={setIsLibraryOpen}
        orgId={currentOrg?.id}
        activeFlags={activeFlags}
      />
    </>
  );
}
